import { useEffect, useState } from 'react';
import type { OpenSlotConfig } from '@jordan/types';
import {
  Alert,
  Breadcrumb,
  Button,
  Input,
  PageHeader,
  ReminderPicker,
  SettingsPanel,
  SettingsRow,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiGet, apiPut } from '../lib/api';

export function OpenSlotSettingsPage() {
  const [cfg, setCfg] = useState<OpenSlotConfig | null>(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<OpenSlotConfig>('/api/config/openslot')
      .then(setCfg)
      .catch(() => setError('Failed to load Open Slot settings.'));
  }, []);

  const set = <K extends keyof OpenSlotConfig>(k: K, v: OpenSlotConfig[K]) =>
    setCfg((p) => (p ? { ...p, [k]: v } : p));
  const num = (k: keyof OpenSlotConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(k, Number(e.target.value) as never);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<OpenSlotConfig>('/api/config/openslot', cfg);
      setCfg(saved);
      setMessage('Open Slot defaults saved.');
    } catch {
      setError('Could not save — check the values are within range.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">Open Slot</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'Open Slot' }]} />
        }
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        {!cfg ? (
          <Spinner />
        ) : (
          <SettingsPanel
            title="Defaults for new events"
            description="Applied as the starting values when you create a new scheduling event. Changing these does not affect existing events."
          >
            <SettingsRow title="Default duration" description="Length of a new event type (minutes).">
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={cfg.defaultDuration} onChange={num('defaultDuration')} />
              </div>
            </SettingsRow>

            <SettingsRow title="Default buffers" description="Padding kept free before and after each booking (minutes).">
              <div className="d-flex gap-3" style={{ maxWidth: 320 }}>
                <Input label="Before" type="number" value={cfg.defaultBufferBefore} onChange={num('defaultBufferBefore')} />
                <Input label="After" type="number" value={cfg.defaultBufferAfter} onChange={num('defaultBufferAfter')} />
              </div>
            </SettingsRow>

            <SettingsRow title="Default minimum notice" description="Least lead time before a booking can start (minutes).">
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={cfg.defaultMinNotice} onChange={num('defaultMinNotice')} />
              </div>
            </SettingsRow>

            <SettingsRow title="Default time slot interval" description="Spacing between offered start times (minutes).">
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={cfg.defaultSlotInterval} onChange={num('defaultSlotInterval')} />
              </div>
            </SettingsRow>

            <SettingsRow title="Default booking window" description="How many days ahead invitees can book.">
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={cfg.defaultRangeDays} onChange={num('defaultRangeDays')} />
              </div>
            </SettingsRow>

            <SettingsRow title="Default reminders" description="Notifications added to new events' calendar entries.">
              <ReminderPicker value={cfg.defaultReminders} onChange={(v) => set('defaultReminders', v)} />
            </SettingsRow>

            <SettingsRow title="" description="">
              <Button variant="primary" disabled={busy} onClick={save}>
                Save changes
              </Button>
            </SettingsRow>
          </SettingsPanel>
        )}
      </div>
    </AppChrome>
  );
}
